import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import LocalBarOutlinedIcon from "@mui/icons-material/LocalBarOutlined";
import { MenuItem } from "@mui/material";
import { useGetBanquetServicesMutation } from "../../../Api/attoDeskApi";
import { AddonItem, BookingFormState, PaymentEntry, PAYMENT_METHODS, SECURITY_METHODS, computeFinancials, fmtCAD } from "./bookingTypes";
import BookingAddOnServices from "./BookingAddOnServices";
import BookingReviewSummary from "./BookingReviewSummary";

// ── Props ──────────────────────────────────────────────────────────────────────
interface Props {
  form: BookingFormState;
  setForm: React.Dispatch<React.SetStateAction<BookingFormState>>;
}

interface ServiceItem {
  id: number;
  category: string;
  name: string;
  price: number;
}

const ADDON_CATEGORIES = [
  "All",
  "Chairs & Tables",
  "Audio & Visual",
  "Transportation",
  "Entertainment",
  "Flowers",
  "Event Decor",
  "Officiant",
  "Beauty",
  "The Little Extras",
  "Additional Services",
] as const;

// ── Component ──────────────────────────────────────────────────────────────────
const Step3Addons = ({ form, setForm }: Props) => {
  const [catalogue, setCatalogue] = useState<ServiceItem[]>([]);
  const [catFilter, setCatFilter] = useState<string>("All");
  const [search, setSearch]       = useState("");
  const [getServices]             = useGetBanquetServicesMutation();

  const fetchCatalogue = useCallback(async () => {
    try {
      const res = (await getServices({}).unwrap()) as any;
      if (res.status && Array.isArray(res.data)) {
        setCatalogue(
          (res.data as any[])
            .filter((s) => s.serviceType === "addon")
            .map((s) => ({
              id: s.id,
              category: s.category ?? "Other",
              name: s.name,
              price: Number(s.price),
            }))
        );
      }
    } catch { /* silently ignore */ }
  }, [getServices]);

  useEffect(() => {
    fetchCatalogue();
  }, [fetchCatalogue]);

  // ── Filtered catalogue ────────────────────────────────────────────────────
  const filtered = useMemo(
    () =>
      catalogue.filter((item) => {
        const matchCat  = catFilter === "All" || item.category === catFilter;
        const matchText =
          !search || item.name.toLowerCase().includes(search.toLowerCase());
        return matchCat && matchText;
      }),
    [catalogue, catFilter, search]
  );

  // ── Cart helpers ──────────────────────────────────────────────────────────
  const cartMap = useMemo(() => {
    const map: Record<number, number> = {};
    form.addons.forEach((a) => {
      if (a.service_id != null) map[a.service_id] = a.quantity;
    });
    return map;
  }, [form.addons]);

  const addItem = (item: ServiceItem) => {
    setForm((prev) => {
      const existing = prev.addons.find((a) => a.service_id === item.id);
      if (existing) {
        return {
          ...prev,
          addons: prev.addons.map((a) =>
            a.service_id === item.id
              ? { ...a, quantity: a.quantity + 1, lineTotal: (a.quantity + 1) * a.unitPrice }
              : a
          ),
        };
      }
      return {
        ...prev,
        addons: [
          ...prev.addons,
          {
            service_id: item.id,
            itemName:   item.name,
            category:   item.category,
            quantity:   1,
            unitPrice:  item.price,
            lineTotal:  item.price,
          },
        ],
      };
    });
  };

  const removeItem = (serviceId: number) => {
    setForm((prev) => {
      const existing = prev.addons.find((a) => a.service_id === serviceId);
      if (!existing) return prev;
      if (existing.quantity > 1) {
        return {
          ...prev,
          addons: prev.addons.map((a) =>
            a.service_id === serviceId
              ? { ...a, quantity: a.quantity - 1, lineTotal: (a.quantity - 1) * a.unitPrice }
              : a
          ),
        };
      }
      return { ...prev, addons: prev.addons.filter((a) => a.service_id !== serviceId) };
    });
  };

  const deleteItem = (serviceId: number | null, idx: number) => {
    setForm((prev) => ({
      ...prev,
      addons: prev.addons.filter((_, i) => i !== idx),
    }));
  };

  const updateQty = (idx: number, qty: number) => {
    if (qty < 1) return;
    setForm((prev) => ({
      ...prev,
      addons: prev.addons.map((a, i) =>
        i === idx ? { ...a, quantity: qty, lineTotal: qty * a.unitPrice } : a
      ),
    }));
  };

  const updatePrice = (idx: number, price: number) => {
    setForm((prev) => ({
      ...prev,
      addons: prev.addons.map((a, i) =>
        i === idx ? { ...a, unitPrice: price, lineTotal: a.quantity * price } : a
      ),
    }));
  };

  // ── Totals ────────────────────────────────────────────────────────────────
  const addonsTotal = form.addons.reduce((s, a) => s + a.lineTotal, 0);

  const fieldSx = { "& .MuiInputBase-root": { fontSize: 13 } };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>

      {/* ════════════════════════════════════════════════════════════════════
         SECTION A — IN-HOUSE ADD-ONS
         ════════════════════════════════════════════════════════════════════ */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "#F5EDE4", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <HomeWorkOutlinedIcon sx={{ fontSize: 16, color: "#D6B98C" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#2C2C2C" }}>In-House Add-ons</Typography>
            <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>Internal offerings — chairs, decor, entertainment extras</Typography>
          </Box>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, borderColor: "#EAE3D9" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Add-on Catalogue
            </Typography>
          <Badge badgeContent={form.addons.length} color="primary" sx={{ ml: "auto" }}>
            <ShoppingCartIcon color="action" fontSize="small" />
          </Badge>
        </Box>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search items…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5, maxWidth: 280, ...fieldSx }}
        />

        {/* Category chips */}
        <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: "wrap", rowGap: 0.5 }}>
          {ADDON_CATEGORIES.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              size="small"
              clickable
              color={catFilter === cat ? "primary" : "default"}
              variant={catFilter === cat ? "filled" : "outlined"}
              onClick={() => setCatFilter(cat)}
              sx={{ fontSize: 11 }}
            />
          ))}
        </Stack>

        {/* Items grid */}
        {filtered.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No items match.
          </Typography>
        ) : (
          <Grid container spacing={1}>
            {filtered.map((item) => {
              const qty = cartMap[item.id] ?? 0;
              return (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      borderColor: qty > 0 ? "primary.main" : "divider",
                      bgcolor: qty > 0 ? "primary.50" : "background.paper",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 12, fontWeight: qty > 0 ? 600 : 400, lineHeight: 1.3 }}
                        noWrap
                      >
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ${item.price.toFixed(0)}
                      </Typography>
                    </Box>
                    {qty === 0 ? (
                      <Tooltip title="Add to booking">
                        <IconButton size="small" color="primary" onClick={() => addItem(item)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => removeItem(item.id)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={{ minWidth: 16, textAlign: "center", fontWeight: 600 }}>
                          {qty}
                        </Typography>
                        <IconButton size="small" color="primary" onClick={() => addItem(item)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      {/* ── Cart (selected add-ons) ── */}
      {form.addons.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderColor: "#EAE3D9" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Selected Add-ons
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12 } }}>
                  <TableCell>Item</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="center" sx={{ width: 110 }}>Qty</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell sx={{ width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {form.addons.map((addon, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontSize: 12 }}>{addon.itemName}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{addon.category}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12 }}>
                      <TextField
                        type="number"
                        size="small"
                        value={addon.unitPrice}
                        onChange={(e) => updatePrice(idx, Number(e.target.value))}
                        inputProps={{ min: 0, step: 0.01, style: { width: 60, fontSize: 12, textAlign: "right" } }}
                        variant="standard"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => updateQty(idx, addon.quantity - 1)}>
                          <RemoveIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <Typography variant="body2" sx={{ minWidth: 20, textAlign: "center", fontSize: 12 }}>
                          {addon.quantity}
                        </Typography>
                        <IconButton size="small" onClick={() => updateQty(idx, addon.quantity + 1)}>
                          <AddIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>
                      {fmtCAD(addon.lineTotal)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteItem(addon.service_id, idx)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell colSpan={4} sx={{ fontSize: 13, fontWeight: 700 }}>
                    Add-ons Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700 }}>
                    {fmtCAD(addonsTotal)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      </Box>

      {/* ════════════════════════════════════════════════════════════════════
         SECTION B — VENDOR SERVICES
         ════════════════════════════════════════════════════════════════════ */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "#EAE3D9", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <StorefrontOutlinedIcon sx={{ fontSize: 16, color: "#5A4633" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#2C2C2C" }}>Vendor Services</Typography>
            <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>External vendor-based services — auto-selects best option</Typography>
          </Box>
        </Box>

        <BookingAddOnServices form={form} setForm={setForm} />
      </Box>

      {/* ════════════════════════════════════════════════════════════════════
         SECTION C — BANQUET ORDER SUMMARY
         ════════════════════════════════════════════════════════════════════ */}
      <BookingReviewSummary form={form} />

      {/* ════════════════════════════════════════════════════════════════════
         SECTION D — FINANCIAL INPUTS & PAYMENTS
         ════════════════════════════════════════════════════════════════════ */}
      <FinancialSection form={form} setForm={setForm} />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FINANCIAL SECTION (moved from Step 2)
   ═══════════════════════════════════════════════════════════════════════════ */
const FT = {
  navy: "#0d1b2a", gold: "#c9a96e", bg: "#f7f5f1", white: "#fff",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  peach: "#ffebd3", redTx: "#9b2c2c", doneTx: "#2d6a35",
};

const finInputSx = {
  "& .MuiInputBase-root": {
    fontSize: 13.5, borderRadius: "10px", bgcolor: "#f8f7f5",
    border: `1px solid #eeebe6`,
    "&.Mui-focused": { bgcolor: "#ffebd3", borderColor: "#c9a96e" },
    transition: "all 0.15s", "& fieldset": { border: "none" },
  },
  "& .MuiInputLabel-root": { fontSize: 13 },
};

const FinancialSection: React.FC<{ form: BookingFormState; setForm: React.Dispatch<React.SetStateAction<BookingFormState>> }> = ({ form, setForm }) => {
  const n = (v: string | number) => Number(v) || 0;
  const set = (field: keyof BookingFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [field]: e.target.value }));
  const setVal = (field: keyof BookingFormState, value: string) => setForm(p => ({ ...p, [field]: value }));

  const addPayment = () => setForm(p => ({ ...p, payments: [...p.payments, { amount: "", method: "Cash", paymentDate: "" }] }));
  const updatePayment = (idx: number, field: keyof PaymentEntry, value: string) =>
    setForm(p => ({ ...p, payments: p.payments.map((pay, i) => i === idx ? { ...pay, [field]: value } : pay) }));
  const removePayment = (idx: number) => setForm(p => ({ ...p, payments: p.payments.filter((_, i) => i !== idx) }));

  const fin = computeFinancials(form);
  const totalDeposits = form.payments.reduce((s, p) => s + n(p.amount), 0);
  const balance = fin.grandTotal - totalDeposits;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

      {/* ══════════ FINANCIAL SUMMARY ══════════ */}
      <Paper elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${FT.line}`, overflow: "hidden" }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 0, display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: FT.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 16, color: FT.gold }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: FT.navy }}>Financial Summary</Typography>
            <Typography sx={{ fontSize: 11, color: FT.slateLt }}>Adjustments, charges, and totals</Typography>
          </Box>
        </Box>
        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={6} sm={3}>
              <TextField label="Adjustment ($)" type="number" size="small" fullWidth value={form.adjustment} onChange={set("adjustment")} sx={finInputSx} />
            </Grid>
            <Grid item xs={6} sm={5}>
              <TextField label="Adjustment Reason" size="small" fullWidth value={form.adjustmentReason} onChange={set("adjustmentReason")} sx={finInputSx} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField label="Service %" type="number" size="small" fullWidth value={form.serviceChargePercent} onChange={set("serviceChargePercent")} inputProps={{ min: 0 }} sx={finInputSx} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField label="Gratuity %" type="number" size="small" fullWidth value={form.gratuityPercent} onChange={set("gratuityPercent")} inputProps={{ min: 0 }} sx={finInputSx} />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, background: `linear-gradient(180deg, ${FT.bg} 0%, #f5f2ed 100%)`, borderRadius: "14px", p: 2.5, border: `1px solid ${FT.line}` }}>
            {[
              { label: "Hall", value: fin.hallSubtotal, icon: <MeetingRoomOutlinedIcon sx={{ fontSize: 13 }} /> },
              { label: "Food (service + menu)", value: fin.foodSubtotal, icon: <RestaurantOutlinedIcon sx={{ fontSize: 13 }} /> },
              { label: "Bar", value: fin.barSubtotal, icon: <LocalBarOutlinedIcon sx={{ fontSize: 13 }} /> },
              ...(n(form.adjustment) !== 0 ? [{ label: "Adjustment", value: n(form.adjustment), icon: undefined }] : []),
              ...(n(form.serviceChargePercent) > 0 ? [{ label: `Service (${form.serviceChargePercent}%)`, value: fin.serviceChargeAmount, icon: undefined }] : []),
              ...(n(form.gratuityPercent) > 0 ? [{ label: `Gratuity (${form.gratuityPercent}%)`, value: fin.gratuityAmount, icon: undefined }] : []),
            ].map((row, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: "7px", px: 0.8, borderRadius: "8px", "&:hover": { bgcolor: `${FT.white}80` } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  {row.icon && <Box sx={{ width: 22, height: 22, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: row.value > 0 ? `${FT.gold}12` : FT.bg, color: row.value > 0 ? FT.gold : FT.slateLt, flexShrink: 0 }}>{row.icon}</Box>}
                  <Typography sx={{ fontSize: 13, color: FT.slateLt }}>{row.label}</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: row.value > 0 ? FT.navy : FT.slateLt, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(row.value)}</Typography>
              </Box>
            ))}
            <Box sx={{ height: 1, background: `linear-gradient(90deg, transparent, ${FT.line}, transparent)`, my: 1.2 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", py: "5px", px: 0.8 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: FT.slate }}>Subtotal</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: FT.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(fin.subtotal)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px", px: 0.8 }}>
              <Typography sx={{ fontSize: 12, color: FT.slateLt }}>HST (13%)</Typography>
              <Typography sx={{ fontSize: 12, color: FT.slate, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(fin.hstAmount)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, py: 1.5, px: 2, background: `linear-gradient(135deg, ${FT.peach} 0%, #fff0d9 100%)`, borderRadius: "12px", border: `1px solid ${FT.gold}20` }}>
              <Box>
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: FT.gold, textTransform: "uppercase", letterSpacing: "0.1em" }}>Grand Total</Typography>
                <Typography sx={{ fontSize: 10, color: FT.slateLt }}>Inc. HST</Typography>
              </Box>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: FT.navy, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(fin.grandTotal)}</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ══════════ SECURITY DEPOSIT ══════════ */}
      <Paper elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${FT.line}`, overflow: "hidden" }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 0, display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: FT.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 16, color: FT.gold }} />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: FT.navy }}>Security Deposit</Typography>
        </Box>
        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={6} sm={3}>
              <TextField label="Amount ($)" type="number" size="small" fullWidth value={form.securityDepositAmount} onChange={set("securityDepositAmount")} inputProps={{ min: 0, step: "any" }} sx={finInputSx} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select label="Method" size="small" fullWidth value={form.securityDepositMethod} onChange={(e) => setVal("securityDepositMethod", e.target.value)} sx={finInputSx}>
                {SECURITY_METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Date" type="date" size="small" fullWidth value={form.securityDepositDate} onChange={set("securityDepositDate")} InputLabelProps={{ shrink: true }} sx={finInputSx} />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* ══════════ DEPOSITS & PAYMENTS ══════════ */}
      <Paper elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${FT.line}`, overflow: "hidden" }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: FT.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <PaymentsOutlinedIcon sx={{ fontSize: 16, color: FT.gold }} />
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: FT.navy }}>Deposits & Payments</Typography>
          </Box>
          <Button size="small" startIcon={<AddIcon />} onClick={addPayment}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 500, color: FT.slate, borderRadius: "8px", bgcolor: FT.bg, "&:hover": { bgcolor: FT.peach } }}>
            Add Payment
          </Button>
        </Box>
        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          {form.payments.length === 0 && (
            <Typography sx={{ fontSize: 12, color: FT.slateLt }}>No deposits recorded yet.</Typography>
          )}
          {form.payments.map((p, idx) => (
            <Grid container spacing={2} key={idx} sx={{ mb: 1.5 }} alignItems="center">
              <Grid item xs={5} sm={3}>
                <TextField label="Amount ($)" type="number" size="small" fullWidth value={p.amount}
                  onChange={(e) => updatePayment(idx, "amount", e.target.value)} inputProps={{ min: 0, step: "any" }} sx={finInputSx} />
              </Grid>
              <Grid item xs={5} sm={3}>
                <TextField select label="Method" size="small" fullWidth value={p.method}
                  onChange={(e) => updatePayment(idx, "method", e.target.value)} sx={finInputSx}>
                  {PAYMENT_METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={10} sm={4}>
                <TextField label="Date" type="date" size="small" fullWidth value={p.paymentDate}
                  onChange={(e) => updatePayment(idx, "paymentDate", e.target.value)} InputLabelProps={{ shrink: true }} sx={finInputSx} />
              </Grid>
              <Grid item xs={2} sm={1}>
                <IconButton size="small" onClick={() => removePayment(idx)} sx={{ color: FT.redTx, "&:hover": { bgcolor: "#fdf0ef" } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          {form.payments.length > 0 && (
            <Box sx={{ mt: 2, display: "flex", gap: 4 }}>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: FT.slateLt, textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Deposits</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: FT.navy }}>{fmtCAD(totalDeposits)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: FT.slateLt, textTransform: "uppercase", letterSpacing: "0.06em" }}>Balance Due</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: balance > 0 ? FT.redTx : balance < 0 ? "#1d4ed8" : FT.doneTx }}>
                  {balance < 0 ? `${fmtCAD(Math.abs(balance))} overpaid` : fmtCAD(balance)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Step3Addons;
